/**
 * Dev-only side panel pinned to the right edge of the chat layout.
 * Hosts CSK rotation debug info for the active chat.
 */

import { useEffect, useState } from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "@/indexedDB/db";
import { getChatFromLocalDB } from "@/indexedDB/services/chats";
import { getChatHotCacheStats } from "@/middleware/epochKeyCache";
import { useNavigationState } from "@/zustand/NavigationState";
import { useDebugRotationState } from "@/zustand/DebugRotationState";
import {
  generateAndAddBotMembers,
  type BotProgress,
} from "@/middleware/debug/botMembers";
import { Button } from "@primitives/button";
import { MemberRole } from "@/server/types/member";

const ROLE_LABELS: Record<MemberRole, string> = {
  [MemberRole.OWNER]: "owner",
  [MemberRole.ADMIN]: "admin",
  [MemberRole.MODERATOR]: "moderator",
  [MemberRole.MEMBER]: "member",
};

const formatBytes = (b: number) =>
  b < 1024
    ? `${b} B`
    : b < 1024 * 1024
      ? `${(b / 1024).toFixed(1)} KB`
      : `${(b / (1024 * 1024)).toFixed(2)} MB`;

const formatMs = (ms: number) =>
  ms < 1
    ? `${(ms * 1000).toFixed(0)} µs`
    : ms < 1000
      ? `${ms.toFixed(1)} ms`
      : `${(ms / 1000).toFixed(2)} s`;

export default function DebugPanel() {
  const activeChatId = useNavigationState((s) => s.activeChatId);
  const activeChatRole = useNavigationState((s) => s.activeChatRoleAsMember);
  const canAddMembers = activeChatRole <= MemberRole.MODERATOR;

  const chat = useLiveQuery(
    () => (activeChatId ? getChatFromLocalDB(activeChatId) : undefined),
    [activeChatId],
  );

  // Hot cache (in-memory) — poll because Maps don't notify
  const [hotBytes, setHotBytes] = useState(0);
  const [hotEntries, setHotEntries] = useState(0);
  useEffect(() => {
    if (!activeChatId) {
      setHotBytes(0);
      setHotEntries(0);
      return;
    }
    const tick = () => {
      const s = getChatHotCacheStats(activeChatId);
      setHotBytes(s.bytes);
      setHotEntries(s.entries);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [activeChatId]);

  const lastRotation = useDebugRotationState((s) =>
    activeChatId ? s.byChat[activeChatId] : undefined,
  );

  // Bot member generator
  const [botCount, setBotCount] = useState(100);
  const [botRunning, setBotRunning] = useState(false);
  const [botProgress, setBotProgress] = useState<BotProgress | null>(null);
  const [botResult, setBotResult] = useState<string | null>(null);

  const runBots = async () => {
    if (!activeChatId || botRunning) {
      return;
    }
    setBotRunning(true);
    setBotProgress(null);
    setBotResult(null);
    const t0 = performance.now();
    try {
      const r = await generateAndAddBotMembers(
        activeChatId,
        botCount,
        setBotProgress,
      );
      const elapsed = ((performance.now() - t0) / 1000).toFixed(1);
      setBotResult(
        `+${r.added.length} added, ${r.errors.length} errors in ${elapsed}s` +
          (r.errors[0]
            ? ` — ${r.errors[0].phase}: ${r.errors[0].message}`
            : ""),
      );
    } catch (e) {
      setBotResult(e instanceof Error ? e.message : String(e));
    } finally {
      setBotRunning(false);
      setBotProgress(null);
    }
  };

  // Warm cache (IndexedDB) — live for this chat
  const warm = useLiveQuery(async () => {
    if (!activeChatId) {
      return { rows: 0, bytes: 0 };
    }
    const rows = await db.epochPayloads
      .where("chatId")
      .equals(activeChatId)
      .toArray();
    let bytes = 0;
    for (const r of rows) {
      bytes +=
        r.id.length * 2 + r.chatId.length * 2 + r.selfECSK.length * 2 + 16;
    }
    return { rows: rows.length, bytes };
  }, [activeChatId]);

  if (!import.meta.env.DEV) {
    return null;
  }

  const totalBytes = hotBytes + (warm?.bytes ?? 0);

  return (
    <div className="font-mono hidden h-full w-64 shrink-0 border-l border-border bg-card p-3 text-xs lg:block">
      <div className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        Debug
      </div>

      {!activeChatId ? (
        <div className="text-muted-foreground">No active chat</div>
      ) : (
        <section className="space-y-2">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            CSK rotation
          </div>
          <Row label="role" value={ROLE_LABELS[activeChatRole] ?? "?"} />
          <Row label="epoch" value={chat?.currentEpoch ?? "?"} />
          <Row
            label="hot keys"
            value={`${hotEntries} · ${formatBytes(hotBytes)}`}
          />
          <Row
            label="warm keys"
            value={warm ? `${warm.rows} · ${formatBytes(warm.bytes)}` : "…"}
          />
          <Row label="total" value={formatBytes(totalBytes)} bold />

          <div className="mt-4 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Last rotation
          </div>
          {lastRotation ? (
            <>
              <Row label="members" value={String(lastRotation.members)} />
              <Row
                label="decrypt PrK"
                value={formatMs(lastRotation.decryptPrivateKey)}
              />
              <Row label="gen CSK" value={formatMs(lastRotation.generateCSK)} />
              <Row
                label="encapsulate"
                value={
                  lastRotation.members > 0
                    ? `${formatMs(lastRotation.encapsulateAll)} (${formatMs(
                        lastRotation.encapsulateAll / lastRotation.members,
                      )} ea)`
                    : formatMs(lastRotation.encapsulateAll)
                }
              />
              <Row label="POST" value={formatMs(lastRotation.serverPost)} />
              <Row
                label="self-encap"
                value={formatMs(lastRotation.selfEncapsulate)}
              />
              <Row label="total" value={formatMs(lastRotation.total)} bold />
            </>
          ) : (
            <div className="text-muted-foreground">No rotation yet</div>
          )}

          <div className="mt-4 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Bot members
          </div>
          <div className="flex items-center gap-1">
            <input
              type="number"
              min={1}
              max={100000}
              value={botCount}
              disabled={botRunning || !canAddMembers}
              onChange={(e) =>
                setBotCount(
                  Math.max(1, Math.min(100000, Number(e.target.value) || 1)),
                )
              }
              className="rounded font-mono w-20 border border-border bg-background px-1 py-0.5 text-xs disabled:opacity-50"
            />
            <Button
              size="sm"
              variant="outline"
              disabled={botRunning || !canAddMembers}
              onClick={runBots}
              title={
                canAddMembers
                  ? undefined
                  : "Need moderator role or higher to add members"
              }
              className="h-7 flex-1 text-xs"
            >
              {botRunning ? "Running…" : `Add ${botCount} bots`}
            </Button>
          </div>
          {!canAddMembers && (
            <div className="text-[11px] text-muted-foreground">
              Needs moderator+ role
            </div>
          )}
          {botProgress && (
            <div className="text-muted-foreground">
              {botProgress.phase} {botProgress.done}/{botProgress.total}
            </div>
          )}
          {botResult && !botRunning && (
            <div className="break-words text-[11px]">{botResult}</div>
          )}
        </section>
      )}
    </div>
  );
}

function Row({
  label,
  value,
  bold,
}: {
  label: string;
  value: React.ReactNode;
  bold?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-muted-foreground">{label}</span>
      <span className={bold ? "font-semibold" : undefined}>{value}</span>
    </div>
  );
}
